import LegalLayout, { LegalSection } from "../components/LegalLayout";
import { BRAND } from "../data/content";
import { useLang, useT } from "../i18n/LanguageContext";

export default function CancellationPolicy() {
  const { lang } = useLang();
  const t = useT();
  const it = lang === "it";
  return (
    <LegalLayout
      title={it ? "Politica di cancellazione" : "Cancellation Policy"}
      subtitle={it
        ? "Termini applicabili alla cancellazione o modifica delle prenotazioni confermate."
        : "Terms applicable to the cancellation or modification of confirmed bookings."}
      seoTitle={t("seo.cancellation.title")}
      seoDesc={t("seo.cancellation.desc")}
      path="/cancellation-policy"
    >
      <LegalSection>
        {it ? (
          <p>Ogni richiesta di cancellazione o modifica di una prenotazione confermata deve essere inviata per iscritto via email
            (<a href={`mailto:${BRAND.email}`} className="text-[#C5A059] underline underline-offset-2">{BRAND.email}</a>)
            o WhatsApp ({BRAND.whatsapp}).</p>
        ) : (
          <p>Any request to cancel or modify a confirmed booking must be submitted in writing via email
            (<a href={`mailto:${BRAND.email}`} className="text-[#C5A059] underline underline-offset-2">{BRAND.email}</a>)
            or WhatsApp ({BRAND.whatsapp}).</p>
        )}
      </LegalSection>

      <LegalSection title={it ? "1. Penali di cancellazione" : "1. Cancellation Fees"}>
        {it ? (
          <>
            <p>In conformità agli standard professionali del settore, si applicano i seguenti addebiti in base al preavviso fornito rispetto all'orario previsto del servizio:</p>
            <ul className="list-none space-y-4 mt-4">
              <li className="border-l-2 border-[#C5A059]/50 pl-4">
                <strong className="text-white">Fino a 48 ore</strong> prima dell'orario di inizio previsto —{" "}
                <span className="text-[#C5A059]">Cancellazione gratuita</span> (rimborso totale, al netto di eventuali commissioni bancarie o di transazione).
              </li>
              <li className="border-l-2 border-[#C5A059]/50 pl-4">
                <strong className="text-white">Tra 48 e 24 ore</strong> prima dell'orario di inizio previsto —{" "}
                <span className="text-[#C5A059]">penale del 50%</span> dell'importo totale stimato.
              </li>
              <li className="border-l-2 border-[#C5A059]/50 pl-4">
                <strong className="text-white">Meno di 24 ore</strong> prima dell'orario di inizio previsto, oppure
                No-Show — <span className="text-[#C5A059]">penale del 100%</span> dell'importo totale stimato.
              </li>
            </ul>
          </>
        ) : (
          <>
            <p>In accordance with professional industry standards, the following charges apply based on the
              notice given prior to the scheduled service time:</p>
            <ul className="list-none space-y-4 mt-4">
              <li className="border-l-2 border-[#C5A059]/50 pl-4">
                <strong className="text-white">Up to 48 hours</strong> before the scheduled start time —{" "}
                <span className="text-[#C5A059]">Free cancellation</span> (full refund, minus any banking or
                transaction fees).
              </li>
              <li className="border-l-2 border-[#C5A059]/50 pl-4">
                <strong className="text-white">Between 48 and 24 hours</strong> before the scheduled start time —{" "}
                <span className="text-[#C5A059]">50% penalty</span> of the total estimated amount.
              </li>
              <li className="border-l-2 border-[#C5A059]/50 pl-4">
                <strong className="text-white">Less than 24 hours</strong> before the scheduled start time, or
                No-Show — <span className="text-[#C5A059]">100% penalty</span> of the total estimated amount.
              </li>
            </ul>
          </>
        )}
      </LegalSection>

      <LegalSection title={it ? "2. Ritardi di voli e treni" : "2. Flight and Train Delays"}>
        {it ? (
          <>
            <p>Per i ritiri in aeroporto o in stazione, monitoriamo attivamente gli orari di voli e treni. Nessuna
              penale verrà applicata per ritardi causati dalle compagnie di trasporto, a condizione che il ritardo
              sia verificabile. Il servizio verrà riprogrammato in base al nuovo orario di arrivo (compatibilmente
              con la disponibilità dei veicoli).</p>
          </>
        ) : (
          <>
            <p>For pick-ups at airports or train stations, we actively monitor flight and train schedules. No
              penalty will be applied for delays caused by transport companies, provided the delay can be
              verified. The service will be rescheduled according to the new arrival time (subject to vehicle
              availability).</p>
          </>
        )}
      </LegalSection>

      <LegalSection title={it ? "3. Arrivi delle navi da crociera" : "3. Cruise Ship Arrivals"}>
        {it ? (
          <>
            <p>Per i servizi con prelievo da navi da crociera, qualora la nave non attracchi nel porto di destinazione
              previsto per cause non imputabili al cliente (ad esempio condizioni meteorologiche avverse, decisioni della
              compagnia di navigazione o modifiche dell'itinerario), il servizio sarà{" "}
              <span className="text-[#C5A059]">cancellato senza alcuna penale</span> e la caparra sarà interamente
              restituita al cliente.</p>
            <p>Tale esenzione è subordinata alla comunicazione del mancato arrivo della nave da parte del cliente, che dovrà
              pervenire per iscritto (email o WhatsApp) <strong className="text-white">entro le ore 22:00 della sera
              precedente al servizio</strong>. In assenza di tale comunicazione entro il termine indicato, la caparra sarà
              trattenuta a titolo di rimborso spese per il viaggio a vuoto dell'autista.</p>
          </>
        ) : (
          <>
            <p>For services involving pick-up from cruise ships, should the vessel fail to dock at the scheduled port of
              call for reasons not attributable to the client (for example adverse weather conditions, decisions of the
              cruise line, or itinerary changes), the service will be{" "}
              <span className="text-[#C5A059]">cancelled without any penalty</span> and the deposit refunded to the
              client in full.</p>
            <p>This exemption is subject to the client notifying us of the ship's non-arrival. Such notice must be given
              in writing (email or WhatsApp) <strong className="text-white">no later than 22:00 (10:00 PM) on the evening
              preceding the service</strong>. In the absence of such notice within the stated deadline, the deposit will
              be retained as reimbursement for the driver's wasted journey.</p>
          </>
        )}
      </LegalSection>
    </LegalLayout>
  );
}
